import { useState, useRef, useEffect, useCallback } from "react";
import { colors } from "../theme";

interface MapNode {
  id: string;
  x: number;
  y: number;
  title: string;
  icon: string;
  description: string;
  details: string[];
  color: string;
}

interface ArchitectureMapProps {
  isDark: boolean;
  onClose: () => void;
}

const nodes: MapNode[] = [
  {
    id: "power-off",
    x: 300,
    y: 350,
    title: "True Power-Off",
    icon: "⏻",
    description: "Hardware-level power control, not just spin-down",
    details: [
      "Drives physically powered off when idle",
      "No 'accidentally wake everything' failures",
      "Zero energy draw from dormant data",
      "Coordinated wake-on-demand",
    ],
    color: colors.cyan,
  },
  {
    id: "smart-scheduling",
    x: 550,
    y: 200,
    title: "Smart Scheduling",
    icon: "◐",
    description: "Intelligent batching minimizes wake cycles",
    details: [
      "Groups retrieval requests efficiently",
      "Only activates what's needed to serve requests",
      "Learns access patterns to pre-position data",
      "Queue optimization for bulk operations",
    ],
    color: colors.blue,
  },
  {
    id: "custom-hardware",
    x: 150,
    y: 500,
    title: "Custom Hardware",
    icon: "⚡",
    description: "Purpose-built for ultra-low power archival",
    details: [
      "Converged storage nodes with tiny footprint",
      "Integrated monitoring and health checks",
      "Single pane of glass integrated sensor monitoring",
      "Designed for density and efficiency",
    ],
    color: colors.violet,
  },
  {
    id: "modularity",
    x: 450,
    y: 500,
    title: "Modular Architecture",
    icon: "▦",
    description: "Self-contained units that scale linearly",
    details: [
      "Independent failure domains",
      "Minimized blast radius",
      "Add capacity without disruption",
      "Petabyte-scale building blocks",
    ],
    color: colors.yellow,
  },
  {
    id: "resilience",
    x: 750,
    y: 350,
    title: "Distributed Resilience",
    icon: "◈",
    description: "Your data survives hardware failures",
    details: [
      "Data spread across many drives",
      "Configurable durability levels",
      "Self-healing when failures occur",
      "Only wake what's needed to reconstruct",
    ],
    color: colors.red,
  },
  {
    id: "efficiency",
    x: 600,
    y: 600,
    title: "Energy Efficiency",
    icon: "♻",
    description: "75% less power than traditional storage",
    details: [
      "Dramatic reduction in idle power draw",
      "Less cooling infrastructure required",
      "Savings passed to customers as data ages",
      "Better for your budget and the planet",
    ],
    color: colors.green,
  },
  {
    id: "retrieval",
    x: 900,
    y: 250,
    title: "Fast Retrieval",
    icon: "↗",
    description: "Hot cache keeps active data fast",
    details: [
      "Frequently-accessed files served instantly",
      "Transparent notifications before cold retrieval",
      "No surprise egress fees",
      "Priority tiers for urgent requests",
    ],
    color: colors.orange,
  },
  {
    id: "indexing",
    x: 350,
    y: 100,
    title: "Archive Search",
    icon: "◎",
    description: "Find your data without waking it",
    details: [
      "Always-on metadata index",
      "Semantic search understands context, not just keywords",
      "Image recognition and audio transcription for searchable media",
      "Automatic content classification",
    ],
    color: colors.magenta,
  },
];

// Connections between nodes
const connections: [string, string][] = [
  ["power-off", "smart-scheduling"],
  ["power-off", "custom-hardware"],
  ["custom-hardware", "modularity"],
  ["modularity", "efficiency"],
  ["smart-scheduling", "indexing"],
  ["smart-scheduling", "retrieval"],
  ["smart-scheduling", "resilience"],
  ["resilience", "retrieval"],
  ["efficiency", "retrieval"],
  ["indexing", "retrieval"],
];

export function ArchitectureMap({ isDark, onClose }: ArchitectureMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [selectedNode, setSelectedNode] = useState<MapNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [containerSize, setContainerSize] = useState({
    width: 1200,
    height: 800,
  });

  // Center the map on load
  useEffect(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
      // Center on the middle of the node cluster
      setOffset({
        x: rect.width / 2 - 550,
        y: rect.height / 2 - 325,
      });
    }
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest(".map-node")) return;
      setIsDragging(true);
      setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    },
    [offset]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      setOffset({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if ((e.target as HTMLElement).closest(".map-node")) return;
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - offset.x,
        y: touch.clientY - offset.y,
      });
    },
    [offset]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      setOffset({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart]
  );

  const getNodePosition = (id: string) => {
    const node = nodes.find((n) => n.id === id);
    return node ? { x: node.x, y: node.y } : { x: 0, y: 0 };
  };

  return (
    <div
      className="fixed inset-0 z-[60] transition-colors duration-[2000ms]"
      style={{
        backgroundColor: isDark ? colors.base03 : colors.base3,
      }}
    >
      {/* Header */}
      <div
        className="absolute top-0 left-0 right-0 z-10 px-8 py-6 flex items-center justify-between transition-colors duration-[2000ms]"
        style={{
          backgroundColor: isDark ? `${colors.base03}E6` : `${colors.base3}E6`,
          backdropFilter: "blur(12px)",
          borderBottom: `1px solid ${isDark ? colors.base02 : colors.base2}`,
        }}
      >
        <div>
          <h1
            className="text-2xl font-semibold"
            style={{ color: isDark ? colors.base0 : colors.base00 }}
          >
            Architecture Explorer
          </h1>
          <p
            className="text-sm mt-1"
            style={{ color: isDark ? colors.base01 : colors.base1 }}
          >
            Click nodes to learn more • Drag to pan
          </p>
        </div>
        <button
          onClick={onClose}
          className="px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300"
          style={{
            backgroundColor: isDark ? colors.base02 : colors.base2,
            color: isDark ? colors.base0 : colors.base00,
          }}
        >
          ← Back to Home
        </button>
      </div>

      {/* Map Canvas */}
      <div
        ref={containerRef}
        className="absolute inset-0 pt-20 overflow-hidden"
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleMouseUp}
      >
        {/* Grid background */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ opacity: 0.3 }}
        >
          <defs>
            <pattern
              id="grid"
              width="50"
              height="50"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 50 0 L 0 0 0 50"
                fill="none"
                stroke={isDark ? colors.base02 : colors.base2}
                strokeWidth="1"
              />
            </pattern>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="url(#grid)"
            style={{
              transform: `translate(${offset.x % 50}px, ${offset.y % 50}px)`,
            }}
          />
        </svg>

        {/* Connections */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
          }}
        >
          {connections.map(([fromId, toId]) => {
            const from = getNodePosition(fromId);
            const to = getNodePosition(toId);
            const isHighlighted =
              hoveredNode === fromId ||
              hoveredNode === toId ||
              selectedNode?.id === fromId ||
              selectedNode?.id === toId;

            return (
              <line
                key={`${fromId}-${toId}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                stroke={
                  isHighlighted
                    ? isDark
                      ? colors.cyan
                      : colors.blue
                    : isDark
                      ? colors.base02
                      : colors.base2
                }
                strokeWidth={isHighlighted ? 3 : 2}
                strokeDasharray={isHighlighted ? "none" : "8 4"}
                style={{ transition: "stroke 0.3s, stroke-width 0.3s" }}
              />
            );
          })}
        </svg>

        {/* Nodes */}
        <div
          className="absolute"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px)`,
          }}
        >
          {nodes.map((node) => (
            <div
              key={node.id}
              className="map-node absolute cursor-pointer transition-transform duration-200 hover:scale-110"
              style={{
                left: node.x,
                top: node.y,
                transform: "translate(-50%, -50%)",
              }}
              onClick={() => setSelectedNode(node)}
              onMouseEnter={() => setHoveredNode(node.id)}
              onMouseLeave={() => setHoveredNode(null)}
            >
              {/* Node circle */}
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-3xl transition-all duration-300"
                style={{
                  backgroundColor:
                    selectedNode?.id === node.id || hoveredNode === node.id
                      ? node.color
                      : isDark
                        ? colors.base02
                        : colors.base2,
                  color:
                    selectedNode?.id === node.id || hoveredNode === node.id
                      ? isDark
                        ? colors.base03
                        : colors.base3
                      : node.color,
                  boxShadow:
                    selectedNode?.id === node.id || hoveredNode === node.id
                      ? `0 0 30px ${node.color}60`
                      : "none",
                  border: `2px solid ${node.color}`,
                }}
              >
                {node.icon}
              </div>

              {/* Node label */}
              <div
                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 whitespace-nowrap text-center"
                style={{ color: isDark ? colors.base0 : colors.base00 }}
              >
                <div className="font-medium text-sm">{node.title}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail Panel - Centered with backdrop */}
      {selectedNode && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-20"
            style={{
              backgroundColor: isDark
                ? `${colors.base03}80`
                : `${colors.base3}80`,
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setSelectedNode(null)}
          />
          {/* Panel */}
          <div
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 w-full max-w-md mx-4 rounded-2xl p-6 transition-colors duration-[2000ms]"
            style={{
              backgroundColor: isDark ? colors.base02 : colors.base2,
              border: `1px solid ${selectedNode.color}40`,
              boxShadow: `0 16px 48px ${isDark ? colors.base03 : colors.base00}60`,
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
                  style={{
                    backgroundColor: `${selectedNode.color}20`,
                    color: selectedNode.color,
                  }}
                >
                  {selectedNode.icon}
                </div>
                <div>
                  <h3
                    className="font-semibold text-lg"
                    style={{ color: isDark ? colors.base0 : colors.base00 }}
                  >
                    {selectedNode.title}
                  </h3>
                  <p
                    className="text-sm"
                    style={{ color: isDark ? colors.base01 : colors.base1 }}
                  >
                    {selectedNode.description}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedNode(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                style={{
                  backgroundColor: isDark ? colors.base03 : colors.base3,
                  color: isDark ? colors.base01 : colors.base1,
                }}
              >
                ×
              </button>
            </div>

            <ul className="space-y-2">
              {selectedNode.details.map((detail, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-sm"
                  style={{ color: isDark ? colors.base0 : colors.base00 }}
                >
                  <span style={{ color: selectedNode.color }}>•</span>
                  {detail}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}

      {/* Zoom hint */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-xs transition-colors duration-[2000ms] md:hidden"
        style={{
          backgroundColor: isDark ? colors.base02 : colors.base2,
          color: isDark ? colors.base01 : colors.base1,
        }}
      >
        Drag to explore
      </div>
    </div>
  );
}
